import type {
  ClaimDetailDto,
  ConflictViewItemDto,
  ConversationMemoryViewDto,
  ProfileSnapshotDto,
  ReadPathLensDto,
} from './introspection-types';
import { maskSubjectId, redactPiiText } from './pii-redaction';

export const CLIENT_DEMO_VIEW = 'client-demo' as const;

export function isClientDemoViewParam(value: string | null | undefined): boolean {
  return value === CLIENT_DEMO_VIEW;
}

export interface SynapseSubjectUserRow {
  userId: string;
  email: string | null;
  inCognito?: boolean;
  hasMemory: boolean;
  profileMaturity: string | null;
  activeClaimCount: number;
  displayLabel?: string;
}

export function sanitizeSubjectUsersForClientDemo(
  users: SynapseSubjectUserRow[]
): SynapseSubjectUserRow[] {
  return users.map((user, index) => ({
    ...user,
    email: null,
    displayLabel: maskSubjectId(user.userId, index),
  }));
}

export function sanitizeProfileSnapshotForClientDemo(
  snapshot: ProfileSnapshotDto,
  subjectIndex?: number
): ProfileSnapshotDto {
  return {
    ...snapshot,
    userId: maskSubjectId(snapshot.userId, subjectIndex),
    topSignals: snapshot.topSignals.map((signal) => redactPiiText(signal)),
  };
}

export function sanitizeClaimDetailForClientDemo(detail: ClaimDetailDto): ClaimDetailDto {
  const sourceContext = detail.sourceContext
    ? {
        ...detail.sourceContext,
        userText: detail.sourceContext.userText ? redactPiiText(detail.sourceContext.userText) : null,
        assistantText: detail.sourceContext.assistantText
          ? redactPiiText(detail.sourceContext.assistantText)
          : null,
        contextTurns: detail.sourceContext.contextTurns.map((turn) => ({
          ...turn,
          userText: redactPiiText(turn.userText),
          assistantText: redactPiiText(turn.assistantText),
        })),
      }
    : null;

  return {
    ...detail,
    evidenceQuotes: detail.evidenceQuotes.map(redactPiiText),
    evidenceDetails: detail.evidenceDetails.map((evidence) => ({
      ...evidence,
      quote: redactPiiText(evidence.quote),
    })),
    sourceContext,
    pipelineGates: [],
    pipelineExpand: null,
    writeTrace: null,
    lifeEvents: detail.lifeEvents.map((event) => ({
      ...event,
      summary: redactPiiText(event.summary),
      detail: event.detail ? redactPiiText(event.detail) : null,
    })),
    relatedConflicts: detail.relatedConflicts.map((conflict) => ({
      ...conflict,
      reason: redactPiiText(conflict.reason),
    })),
    editHistory: detail.editHistory.map((entry) => ({
      ...entry,
      metadata: redactRecordValues(entry.metadata),
    })),
    claim: {
      ...detail.claim,
      fullPayload: redactRecordValues(detail.claim.fullPayload),
    },
  };
}

export function sanitizeConflictsForClientDemo(conflicts: ConflictViewItemDto[]): ConflictViewItemDto[] {
  return conflicts.map((item) => ({
    ...item,
    existingEvidence: item.existingEvidence.map(redactPiiText),
    candidateEvidence: item.candidateEvidence.map(redactPiiText),
    conflict: {
      ...item.conflict,
      reason: redactPiiText(item.conflict.reason),
    },
  }));
}

export function sanitizeConversationForClientDemo(
  view: ConversationMemoryViewDto
): ConversationMemoryViewDto {
  return {
    ...view,
    title: redactPiiText(view.title),
    turns: view.turns.map((turn) => ({
      ...turn,
      userText: redactPiiText(turn.userText),
      assistantText: redactPiiText(turn.assistantText),
    })),
  };
}

function sanitizeReadPathCandidate(
  candidate: ReadPathLensDto['selectedCandidates'][number]
): ReadPathLensDto['selectedCandidates'][number] {
  return {
    ...candidate,
    value: candidate.value ? redactPiiText(candidate.value) : null,
    label: redactPiiText(candidate.label),
    dropReason: candidate.dropReason ? redactPiiText(candidate.dropReason) : null,
  };
}

export function sanitizeReadPathLensForClientDemo(lens: ReadPathLensDto): ReadPathLensDto {
  return {
    ...lens,
    ucbRenderedText: lens.ucbRenderedText ? redactPiiText(lens.ucbRenderedText) : null,
    contextBundleRenderedText: lens.contextBundleRenderedText
      ? redactPiiText(lens.contextBundleRenderedText)
      : null,
    injectedContextText: lens.injectedContextText ? redactPiiText(lens.injectedContextText) : null,
    selectedCandidates: lens.selectedCandidates.map(sanitizeReadPathCandidate),
    droppedCandidates: lens.droppedCandidates.map(sanitizeReadPathCandidate),
  };
}

function redactRecordValues(value: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    result[key] = redactUnknown(entry);
  }
  return result;
}

function redactUnknown(value: unknown): unknown {
  if (typeof value === 'string') {
    return redactPiiText(value);
  }
  if (Array.isArray(value)) {
    return value.map(redactUnknown);
  }
  if (value && typeof value === 'object') {
    return redactRecordValues(value as Record<string, unknown>);
  }
  return value;
}
